import { atom, getDefaultStore, PrimitiveAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import {
  DEFAULT_DETECT_VOLUME,
  TOKEN_INFO_SESSION_STORAGE_KEY,
  CONTRACT_ENGINES_SESSION_STORAGE_KEY,
  RECORDING_SETTING_SESSION_STORAGE_KEY,
} from "./constants";
import { AudioRecorder } from "./utils/audioRecorder/audioRecorder";
import workletUrl from "./assets/js/RecorderProcessor.js?url";
import {
  AccessToken,
  SupportedAnalysisEngineType,
  supportedAnalysisEngineTypes,
  themeType,
} from "./types";
import type { components } from "./types/voislog_api";
import { createEngines } from "./engines/engineFactory";
import { BaseAnalysisEngine } from "./engines/baseAnalysisEngine";

export const store = getDefaultStore();

const DETECT_VOLUME_LOCALSTORAGE_KEY = "detectVolume";

export const detectVolumeAtom = atomWithStorage(
  DETECT_VOLUME_LOCALSTORAGE_KEY,
  DEFAULT_DETECT_VOLUME,
  undefined,
  { getOnInit: true },
);

export const detectedVolumeByPhraseVisualizerAtom = atom(false);

export const themeColorAtom = atomWithStorage<themeType>(
  "themeColor",
  "sakura_light",
  undefined,
  { getOnInit: true },
);

function analysisEngineAtom(
  type: SupportedAnalysisEngineType,
): PrimitiveAtom<BaseAnalysisEngine<unknown>[]> {
  return atom(createEngines(type));
}

export const engineAtomsMap: Record<
  SupportedAnalysisEngineType,
  ReturnType<typeof analysisEngineAtom>
> = Object.fromEntries(
  supportedAnalysisEngineTypes.map((type) => [type, analysisEngineAtom(type)]),
) as Record<SupportedAnalysisEngineType, ReturnType<typeof analysisEngineAtom>>;

// 利用規約の同意状態を管理するAtom
export const termsAgreementAtom = atom<boolean>(false);

const accessTokenStorage = createJSONStorage<AccessToken | null>(
  () => sessionStorage,
);
export const accessTokenInfoAtom = atomWithStorage<AccessToken | null>(
  TOKEN_INFO_SESSION_STORAGE_KEY,
  null,
  accessTokenStorage,
  { getOnInit: true },
);

export type DecodedAccessToken = {
  app_id: components["schemas"]["AppType"];
  email: string;
  user_id: string;
  exp: number;
};

// STENスコアの出力有無のグローバル状態管理用
export const outputStenScoreAtom = atom(false);

// 契約エンジン種別のグローバル状態管理用
const contractEnginesStorage = createJSONStorage<
  components["schemas"]["EngineType"][]
>(() => sessionStorage);
export const contractEnginesAtom = atomWithStorage<
  components["schemas"]["EngineType"][]
>(CONTRACT_ENGINES_SESSION_STORAGE_KEY, [], contractEnginesStorage, {
  getOnInit: true,
});

export const audioRecorder = new AudioRecorder(workletUrl);

export type EmployeeInfoType = {
  company_id: string;
  employee_name: string;
  employee_id: string;
};

export const employeeInfoInitial = {
  company_id: "",
  employee_name: "",
  employee_id: "",
};

export const employeeInfoAtom = atom<EmployeeInfoType>(employeeInfoInitial);

// contractEnginesAtomからの派生（既存の "key" in engineTypes パターン互換）
export type EngineTypesMap = Partial<
  Record<components["schemas"]["EngineType"], boolean>
>;
export const engineTypesAtom = atom<EngineTypesMap>((get) =>
  Object.fromEntries(get(contractEnginesAtom).map((e) => [e, true])),
);

export const co2ClassificationThresholdLow = atom<number>(30);
export const co2ClassificationThresholdHigh = atom<number>(70);

// v_management recording設定のグローバル状態管理用
export type RecordingSettingType =
  components["schemas"]["RecordingSettingsResponseWithDefault"];

const recordingSettingStorage = createJSONStorage<RecordingSettingType | null>(
  () => sessionStorage,
);
export const recordingSettingAtom =
  atomWithStorage<RecordingSettingType | null>(
    RECORDING_SETTING_SESSION_STORAGE_KEY,
    null,
    recordingSettingStorage,
    { getOnInit: true },
  );
